import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BottomLoadingIndicator from "components/common/BottomLoadingIndicator";
import {
  useListAdvisorAdministration,
  useListAdvisorAdministrationMapLocations,
} from "services/admin";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import { MailCheck, Map as MapIcon, Plus } from "lucide-react";

import { Advisor, AdvisorStatus } from "protogen/common_pb";
import {
  AdvisorAdministrationFamily,
  FamilyDirectoryMapLocation,
  FamilyDirectoryMarket,
} from "protogen/admin_service_pb";
import MapList from "components/common/maps/MapList";
import { Treatment } from "components/common/maps/FMarker";
import SearchBar from "components/common/SearchBar";
import AddAdvisorDialog from "components/advisor/AddAdvisorDialog";
import EditAdvisorDialog from "components/advisor/EditAdvisorDialog";
import InviteAdvisorDialog from "components/advisor/InviteAdvisorDialog";
import AdvisorCard from "components/admin/AdvisorCard";
import CheckedDropDown from "components/common/CheckedDropDown";

const statusOptions = [
  { label: "Any status", value: "", mutuallyExclusive: true },
  {
    label: "Active",
    value: AdvisorStatus.ACTIVE.toString(),
    mutuallyExclusive: true,
  },
  {
    label: "Pending",
    value: AdvisorStatus.PENDING.toString(),
    mutuallyExclusive: true,
  },
  {
    label: "Deactivated",
    value: AdvisorStatus.DEACTIVATED.toString(),
    mutuallyExclusive: true,
  },
  {
    label: "Staff",
    value: AdvisorStatus.STAFF.toString(),
    mutuallyExclusive: true,
  },
  {
    label: "Test",
    value: AdvisorStatus.TEST.toString(),
    mutuallyExclusive: true,
  },
];

const locationOptions = (markets: FamilyDirectoryMarket[]) => [
  { label: "Any DMA", value: "", mutuallyExclusive: true },
  ...markets.map((market) => ({
    label: market.name,
    value: market.code.toString(),
    mutuallyExclusive: true,
  })),
];

const getStateColor = (status: AdvisorStatus) => {
  switch (status) {
    case AdvisorStatus.ACTIVE:
      return "#198282";
    case AdvisorStatus.PENDING:
      return "#FFA500";
    case AdvisorStatus.DEACTIVATED:
      return "#FF0000";
    case AdvisorStatus.STAFF:
    case AdvisorStatus.TEST:
    default:
      return "#9E9E9E";
  }
};

export default () => {
  const params = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mapOpen, setMapOpen] = useState(false);
  const [addAdvisorOpen, setAddAdvisorOpen] = useState(false);
  const [inviteAdvisorOpen, setInviteAdvisorOpen] = useState(false);
  const [editAdvisor, setEditAdvisor] = useState<Advisor | null>(null);
  const initialSearchParams = new URLSearchParams(window.location.search);
  const [query, setQuery] = useState(initialSearchParams.get("query") || "");
  const [status, setStatus] = useState(initialSearchParams.get("status") || "");
  const [location, setLocation] = useState(
    initialSearchParams.get("location") || "",
  );
  const [, setSearchParams] = useSearchParams();
  const [initialAdvisorRef] = useState<string | undefined>(params.advisorRef);
  const [advisorRefs, setAdvisorRefs] = useState<string[]>(
    params.advisorRef ? [params.advisorRef] : [],
  );

  const [advisorsMap, setAdvisorsMap] = useState<Map<string, Advisor>>(
    new Map(),
  );
  const [familiesByAdvisorMap, setFamiliesByAdvisorMap] = useState<
    Map<string, AdvisorAdministrationFamily[]>
  >(new Map());
  const [mapLocations, setMapLocations] = useState<
    FamilyDirectoryMapLocation[]
  >([]);
  const [markets, setMarkets] = useState<FamilyDirectoryMarket[]>([]);
  const [nextCursor, setNextCursor] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const requestGeneration = useRef(0);
  const loadingMore = useRef(false);
  const nextCursorRef = useRef("");
  const requestedCursors = useRef(new Set<string>());
  const loadMoreSentinel = useRef<HTMLDivElement | null>(null);
  const pageSize = 25;

  const {
    request: requestSingle,
    loading: loadingSingle,
    cancel: cancelSingle,
  } = useListAdvisorAdministration();
  const {
    request: requestPage,
    loading: loadingPage,
    cancel: cancelPage,
  } = useListAdvisorAdministration();
  const { request: mapRequest } = useListAdvisorAdministrationMapLocations();

  const requestParams = (cursor: string) => ({
    limit: pageSize,
    cursor,
    query,
    advisorRef: "",
    status: status
      ? (parseInt(status) as AdvisorStatus)
      : AdvisorStatus.NOT_SET,
    marketCode: /^\d+$/.test(location) ? parseInt(location) : 0,
  });

  const mergeResponse = (
    advisors: Advisor[],
    advisorFamilies: {
      advisorRef: string;
      families: AdvisorAdministrationFamily[];
    }[],
    append: boolean,
  ) => {
    setAdvisorsMap((current) => {
      const updated = append ? new Map(current) : new Map();
      advisors.forEach((advisor) => updated.set(advisor.ref, advisor));
      return updated;
    });
    setFamiliesByAdvisorMap((current) => {
      const updated = append ? new Map(current) : new Map();
      advisorFamilies.forEach((entry) => {
        updated.set(entry.advisorRef, entry.families);
      });
      return updated;
    });
  };

  useEffect(() => {
    const generation = ++requestGeneration.current;
    loadingMore.current = false;
    nextCursorRef.current = "";
    requestedCursors.current.clear();
    setAdvisorsMap(new Map());
    setFamiliesByAdvisorMap(new Map());
    setNextCursor("");
    setMapOpen(false);
    setMapLocations([]);

    const loadFirstPage = async () => {
      const response = await requestPage(requestParams(""));
      if (!response || generation !== requestGeneration.current) return;
      nextCursorRef.current = response.nextCursor;
      setNextCursor(response.nextCursor);
      mergeResponse(response.advisors, response.advisorFamilies, true);
      setMarkets(response.markets);
    };
    loadFirstPage();

    if (initialAdvisorRef && !query && !status && !location) {
      const loadInitialAdvisor = async () => {
        const response = await requestSingle({
          limit: 1,
          cursor: "",
          query: "",
          advisorRef: initialAdvisorRef,
          status: AdvisorStatus.NOT_SET,
          marketCode: 0,
        });
        if (!response || generation !== requestGeneration.current) return;
        mergeResponse(response.advisors, response.advisorFamilies, true);
      };
      loadInitialAdvisor();
    }

    return () => {
      cancelPage();
      cancelSingle();
    };
  }, [query, status, location, reloadVersion]);

  const refreshAdvisors = useCallback(() => {
    setReloadVersion((current) => current + 1);
  }, []);

  useEffect(() => {
    // Keep the current filter set shareable and reload-safe.
    setSearchParams({
      ...(query ? { query } : {}),
      ...(status ? { status } : {}),
      ...(location ? { location } : {}),
    });
  }, [query, status, location, setSearchParams]);

  // Update URL when lead card is opened/closed
  const handleCardOpen = (ref: string) => {
    setAdvisorRefs([ref]);
    navigate(
      `/tools/advisor-administration/${encodeURIComponent(ref)}${
        window.location.search
      }`,
    );
  };

  const handleCardClose = () => {
    setAdvisorRefs([]);
    navigate(`/tools/advisor-administration${window.location.search}`);
  };

  const resultingAdvisors = useMemo(
    () =>
      Array.from(advisorsMap.values()).sort((a, b) => {
        if (a.ref === initialAdvisorRef) return -1;
        if (b.ref === initialAdvisorRef) return 1;
        return a.displayName.localeCompare(b.displayName);
      }),
    [advisorsMap, initialAdvisorRef],
  );

  const loadNextPage = async () => {
    const cursor = nextCursorRef.current;
    if (!cursor || loadingMore.current || requestedCursors.current.has(cursor))
      return;

    requestedCursors.current.add(cursor);
    loadingMore.current = true;
    const generation = requestGeneration.current;
    try {
      const response = await requestPage(requestParams(cursor));
      if (response && generation === requestGeneration.current) {
        nextCursorRef.current = response.nextCursor;
        setNextCursor(response.nextCursor);
        mergeResponse(response.advisors, response.advisorFamilies, true);
      }
    } finally {
      requestedCursors.current.delete(cursor);
      loadingMore.current = false;
    }
  };

  useEffect(() => {
    const sentinel = loadMoreSentinel.current;
    if (!sentinel || !nextCursor) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadNextPage();
      },
      { rootMargin: "600px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextCursor, query, status, location]);

  const openMap = async () => {
    const generation = requestGeneration.current;
    setMapLocations([]);
    setMapOpen(true);
    const response = await mapRequest({
      query,
      status: status
        ? (parseInt(status) as AdvisorStatus)
        : AdvisorStatus.NOT_SET,
      marketCode: /^\d+$/.test(location) ? parseInt(location) : 0,
    });
    if (response && generation === requestGeneration.current) {
      setMapLocations(response.locations);
    }
  };

  const mapListLocations = useMemo(
    () =>
      mapLocations.map((location) => ({
        title: location.title,
        link: `/advisor/${encodeURIComponent(location.ref)}`,
        content: location.formattedAddress,
        color: getStateColor(location.advisorStatus),
        treatment: Treatment.Pin,
        bounding: true,
        address: location.formattedAddress,
        lat: location.latitude || undefined,
        lng: location.longitude || undefined,
      })),
    [mapLocations],
  );

  const loading = (loadingPage || loadingSingle) && advisorsMap.size === 0;
  const dmaOptions = locationOptions(markets);

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "64px",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              {
                name: "Home",
                link: "/",
              },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Advisor Administration</Typography>
            <Box
              display="flex"
              flexDirection="row"
              gap="8px"
              alignItems={"center"}
            >
              <SearchBar
                initialQuery={query}
                fullWidth={isMobile}
                autofocus={true}
                onQuery={setQuery}
                onKeyPress={true}
                debounceTimeoutMs={300}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap="12px"
            marginTop="12px"
            justifyContent={"space-between"}
          >
            <Box display="flex" flexDirection="row" gap="12px" marginTop="12px">
              <Button
                variant="outlined"
                onClick={() => setAddAdvisorOpen(true)}
                sx={{
                  height: "36px",
                  minWidth: "unset",
                  padding: "0 15px",
                }}
                startIcon={<Plus />}
              >
                Add Advisor
              </Button>
              <Button
                variant="outlined"
                onClick={() => setInviteAdvisorOpen(true)}
                sx={{
                  height: "36px",
                  minWidth: "unset",
                  padding: "0 15px",
                }}
                startIcon={
                  <MailCheck
                    style={{
                      height: "20px",
                      width: "20px",
                    }}
                  />
                }
              >
                Invite Advisor
              </Button>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              marginTop="12px"
              sx={{ overflowX: "auto", flexShrink: 0 }}
            >
              <CheckedDropDown
                disabled={false}
                radio={true}
                initial={[status]}
                options={statusOptions}
                sx={{ minWidth: "120px" }}
                maxDropdownHeight="343px"
                displayValue={
                  (status &&
                    statusOptions.find((option) => option.value === status)
                      ?.label) ||
                  "Status"
                }
                onChange={(filters) =>
                  filters.length === 1 && setStatus(filters[0].value)
                }
              />
              <CheckedDropDown
                disabled={false}
                radio={true}
                initial={[location]}
                options={dmaOptions}
                sx={{ minWidth: "140px" }}
                maxDropdownHeight="343px"
                displayValue={
                  (location &&
                    dmaOptions.find((option) => option.value === location)
                      ?.label) ||
                  "DMA"
                }
                onChange={(filters) =>
                  filters.length === 1 && setLocation(filters[0].value)
                }
              />
              <Button
                onClick={openMap}
                sx={{
                  borderRadius: "100px",
                  border: "2px solid #EAEBEC",
                  color: "#616161",
                  backgroundColor: "unset",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#EAEBEC",
                  },
                }}
                startIcon={<MapIcon size={20} stroke="#8E9598" />}
              >
                Mapped
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flex: "1 1 auto",
          position: "relative",
          gap: "16px",
          padding: !isMobile ? "0" : undefined,
          paddingBottom: loading ? "64px" : undefined,
        }}
      >
        {!isMobile && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="end"
            padding="24px 32px 0 32px"
            mr="34px"
            ml="60px"
          >
            <Box flex={5} display="flex">
              <Typography variant="bodySmallHeavy" color="#262626">
                Name
              </Typography>
            </Box>
            {!isMobile && (
              <>
                <Box flex={2} display="flex" justifyContent="end">
                  <Typography
                    variant="bodySmallHeavy"
                    color="#262626"
                    alignSelf={"end"}
                  >
                    Location
                  </Typography>
                </Box>
                <Box flex={2} display="flex" justifyContent="end">
                  <Typography variant="bodySmallHeavy" color="#262626">
                    Status
                  </Typography>
                </Box>{" "}
              </>
            )}
          </Box>
        )}
        {resultingAdvisors.map((advisor) => (
          <AdvisorCard
            key={advisor.ref}
            advisor={advisor}
            allFamilies={familiesByAdvisorMap.get(advisor.ref) || []}
            familiesLoading={false}
            onEdit={() => setEditAdvisor(advisor)}
            forceOpen={
              resultingAdvisors.length === 1 ||
              advisorRefs.indexOf(advisor.ref) >= 0
            }
            onClose={() => handleCardClose()}
            onOpen={() => handleCardOpen(advisor.ref)}
          />
        ))}
        <div ref={loadMoreSentinel} />
        {(loadingPage || loadingSingle) && (
          <BottomLoadingIndicator label="Loading advisors…" />
        )}
      </Box>
      <InviteAdvisorDialog
        open={inviteAdvisorOpen}
        onClose={() => {
          setInviteAdvisorOpen(false);
        }}
      />
      {editAdvisor && (
        <EditAdvisorDialog
          advisor={editAdvisor}
          open={!!editAdvisor}
          onClose={(updated) => {
            setEditAdvisor(null);
            if (updated) {
              refreshAdvisors();
            }
          }}
        />
      )}
      <AddAdvisorDialog
        open={addAdvisorOpen}
        onClose={(created) => {
          setAddAdvisorOpen(false);
          if (created) {
            refreshAdvisors();
          }
        }}
      />
      <MapList
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        locations={mapListLocations}
      />
    </Box>
  );
};
